import React, { useCallback, useEffect, useState } from 'react';
import { useOutletContext, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import CloseIcon from '@mui/icons-material/Close';
import { listChatMessages, postChatMessage } from '../../api/runs';
import { requestCoordinator } from '../../api/admin';
import { useAuth } from '../../auth/useAuth';
import type { ChatMessage, Team } from '../../api/types';
import type { PublicRunState } from '../../services/publicRuns';
import { useWebSocket } from '../../hooks/useWebSocket';
import ConfirmDialog from '../../components/ConfirmDialog';
import ChatMessageList from '../../components/chat/ChatMessageList';
import ChatComposer from '../../components/chat/ChatComposer';

interface TeamTabOutletContext {
  team: Team | null;
  run: PublicRunState | null;
}

const TeamChat: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const { user } = useAuth();
  const { t } = useTranslation('runs');
  const { t: tAdmin } = useTranslation('admin');
  // Optional: TeamChat is normally nested under TeamTab's Outlet (which
  // supplies `team`), but tests and any other embedding shouldn't crash
  // without it — useOutletContext() is undefined outside a matching Outlet.
  const outlet = useOutletContext<TeamTabOutletContext>();
  const team = outlet?.team ?? null;
  const run = outlet?.run ?? null;

  const orgSlug = user?.organization_slug;
  const teamId = runId
    ? sessionStorage.getItem(`clavis_team_for_run_${runId}`)
    : null;

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [deliveryFailed, setDeliveryFailed] = useState(false);
  const [confirmRequestOpen, setConfirmRequestOpen] = useState(false);
  const [requestingCoordinator, setRequestingCoordinator] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState(false);
  // Whether the coordinator assist window is open — seeded from the team
  // fetch (RunShell → TeamTab → here) so it survives reloads, not just local
  // request state (#11: the button used to silently revert to "Request help"
  // once the success toast dismissed, even though a request was still live).
  const [coordinatorChatOpen, setCoordinatorChatOpen] = useState(
    () => team?.coordinator_chat_open ?? false,
  );
  useEffect(() => {
    if (team) setCoordinatorChatOpen(team.coordinator_chat_open);
  }, [team]);

  const load = useCallback(async () => {
    if (!orgSlug || !runId || !teamId) return;
    try {
      const resp = await listChatMessages(orgSlug, runId, teamId);
      // The endpoint orders by created_at ASC — oldest first, which is already
      // the render order. Reversing it here put the newest message at the top.
      setMessages(resp.items);
    } catch {
      // Initial load failure stays silent in the UI; the WS will retry.
    }
  }, [orgSlug, runId, teamId]);

  useEffect(() => {
    void load();
  }, [load]);

  useWebSocket({
    path:
      orgSlug && runId && teamId
        ? `/api/orgs/${orgSlug}/runs/${runId}/teams/${teamId}/ws`
        : '',
    enabled: !!(orgSlug && runId && teamId),
    handlers: {
      chat_message: (data) => {
        const msg = data as ChatMessage;
        setMessages((prev) => {
          if (prev.some((m) => m.id === msg.id)) return prev;
          return [...prev, msg];
        });
        if (msg.is_system_message && msg.content.startsWith('[coordinator_left]')) {
          setCoordinatorChatOpen(false);
        }
      },
    },
  });

  // The backend only accepts chat while the run is active. Say so in the UI
  // instead of letting someone write a message that comes back rejected — after
  // the 29.07 run ended, six messages were typed and refused in fifty seconds,
  // each answered with an English server string in an otherwise German app
  // (#495). While the state is still loading, `run` is null; stay open rather
  // than flashing a "closed" notice at someone who can in fact write.
  const closedReason =
    run && run.state !== 'active'
      ? t(
          run.state === 'ended'
            ? 'participant.chat.closedEnded'
            : run.state === 'paused'
              ? 'participant.chat.closedPaused'
              : 'participant.chat.closedNotStarted',
        )
      : undefined;

  const canSend =
    !closedReason && !!draft.trim() && !sending && !!(orgSlug && runId && teamId);

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    setDeliveryFailed(false);
    try {
      await postChatMessage(orgSlug!, runId!, teamId!, { content: draft.trim() });
      setDraft('');
      // Message will arrive via WS; if it doesn't (slow link), the next
      // visit will pick it up on reload.
    } catch {
      // Per the design, we surface a friendly banner and keep the draft
      // intact so the user can retry without retyping.
      setDeliveryFailed(true);
    } finally {
      setSending(false);
    }
  };

  const handleRequestCoordinator = async () => {
    if (!orgSlug || !runId || !teamId) return;
    setConfirmRequestOpen(false);
    setRequestingCoordinator(true);
    try {
      await requestCoordinator(orgSlug, runId, teamId);
      setRequestSuccess(true);
      // The backend opens the assist window synchronously on request — reflect
      // that immediately instead of waiting for a refetch.
      setCoordinatorChatOpen(true);
    } catch {
      // Request failed (e.g. network); the user can retry.
    } finally {
      setRequestingCoordinator(false);
    }
  };

  if (!teamId) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography variant="h2" component="h1" gutterBottom>
          {t('participant.chat.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('participant.chat.noTeam')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100dvh - 220px)',
      }}
    >
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h2" component="h1">
          {t('participant.chat.title')}
        </Typography>
        <Button
          variant="outlined"
          color="warning"
          size="small"
          startIcon={<SupportAgentIcon />}
          onClick={() => setConfirmRequestOpen(true)}
          disabled={requestingCoordinator || coordinatorChatOpen}
        >
          {coordinatorChatOpen
            ? tAdmin('coordinatorRequest.pendingLabel')
            : tAdmin('coordinatorRequest.buttonLabel')}
        </Button>
      </Stack>

      {deliveryFailed && (
        <Alert
          severity="error"
          sx={{ mb: 1 }}
          action={
            <Stack direction="row" spacing={0.5}>
              <Button
                color="inherit"
                size="small"
                onClick={() => void handleSend()}
                disabled={sending}
              >
                {t('participant.chat.deliveryFailedRetry')}
              </Button>
              <IconButton
                color="inherit"
                size="small"
                onClick={() => {
                  setDeliveryFailed(false);
                  setDraft('');
                }}
                aria-label={t('participant.chat.deliveryFailedDismiss')}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          }
        >
          {t('participant.chat.deliveryFailed')}
        </Alert>
      )}

      <ChatMessageList messages={messages} currentUserId={user?.id} />

      <ChatComposer
        draft={draft}
        onDraftChange={setDraft}
        onSend={() => void handleSend()}
        canSend={canSend}
        closedReason={closedReason}
      />

      <ConfirmDialog
        open={confirmRequestOpen}
        title={tAdmin('coordinatorRequest.buttonLabel')}
        message={tAdmin('coordinatorRequest.confirmMessage')}
        onConfirm={() => void handleRequestCoordinator()}
        onCancel={() => setConfirmRequestOpen(false)}
      />

      <Snackbar
        open={requestSuccess}
        autoHideDuration={4000}
        onClose={() => setRequestSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setRequestSuccess(false)}>
          {tAdmin('coordinatorRequest.success')}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TeamChat;
